/**
 * Doctor onboarding types - aligned with backend schemas and iOS/Android.
 */

export interface CountryCode {
  id: number;
  country_name: string;
  iso2?: string;
  country_code?: string;
  dial_code: string;
  min_digits: number;
  max_digits: number;
  is_active: boolean;
}

export interface Timezone {
  id: number;
  identifier: string;
  display_name: string;
  is_active: boolean;
}

export interface GenderType {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
}

export interface Specialization {
  id: number;
  name: string;
  is_active: boolean;
  subspecializations?: Subspecialization[];
}

export interface Subspecialization {
  id: number;
  specialization_id: number;
  name: string;
  is_active: boolean;
}

export interface MedicalCouncil {
  id: number;
  name: string;
  code?: string;
  description?: string;
  is_active: boolean;
}

export interface CouncilInfo {
  primary_council: { id: number; name: string; code?: string } | null;
  registration_required: string;
  identifier_type?: string;
}

export interface DegreeType {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

export interface PracticeType {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

export interface CertificationType {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

export interface ServiceCategory {
  id: number;
  name: string;
  display_order: number;
  medical_services?: MedicalService[];
}

export interface MedicalService {
  id: number;
  category_id?: number | null;
  /** Backend returns service_name */
  service_name: string;
  name?: string;
  service_description?: string | null;
  typical_duration?: number | null;
  default_duration_minutes?: number;
  is_active: boolean;
}

/** Profile (personal + professional) - matches backend DoctorProfileCreate/DoctorProfileWithDetails */
export interface DoctorProfile {
  id?: number;
  doctor_id: number;
  title?: string | null;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  date_of_birth: string | null;
  gender: string | null;
  contact_number: string;
  country_code_id?: number | null;
  alternate_contact_number?: string | null;
  bio?: string | null;
  years_experience?: number | null;
  registration_number?: string | null;
  registration_council?: string | null;
  registration_year?: number | null;
  specialization_id?: number | null;
  subspecialization_id?: number | null;
  languages_spoken?: string[];
  timezone_id?: number | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  profile_photo_url?: string | null;
  specialization_name?: string | null;
  subspecialization_name?: string | null;
  timezone_name?: string | null;
  full_name?: string;
  age?: number;
  email?: string | null;
  onboarding_step?: number;
  current_onboarding_step?: number;
  created_at?: string;
  updated_at?: string | null;
}

export interface DoctorEducation {
  id: number;
  doctor_id: number;
  degree_type: string;
  degree_name: string;
  institution_name: string;
  university_name: string;
  year_of_completion: number;
  certificate_document_url: string;
  display_order: number;
  specialization_id?: number | null;
  subspecialization_id?: number | null;
  specialization_name?: string | null;
  subspecialization_name?: string | null;
  is_verified?: boolean;
  created_at?: string;
  updated_at?: string | null;
}

export interface DoctorCertification {
  id: number;
  doctor_id: number;
  certification_type_id: number;
  certification_name: string;
  issuing_organization: string;
  issue_date: number;
  expiry_date?: number | null;
  certificate_number?: string | null;
  certificate_document_url?: string | null;
  specialization_id?: number | null;
  subspecialization_id?: number | null;
  specialization_name?: string | null;
  subspecialization_name?: string | null;
  is_verified?: boolean;
  created_at?: string;
  updated_at?: string | null;
}

export interface DoctorDocument {
  id: number;
  doctor_id: number;
  document_type: string;
  document_subtype?: string | null;
  document_number?: string | null;
  document_url: string;
  is_verified?: boolean;
  created_at?: string;
  updated_at?: string | null;
}

export interface DoctorPracticeTiming {
  id: number;
  practice_id: number;
  day_of_week: string;
  is_available: boolean;
  start_time: string;
  end_time: string;
  slot_duration: number;
  breaks?: Array<{ break_start_time: string; break_end_time: string }>;
  created_at?: string;
  updated_at?: string | null;
}

export interface DoctorPractice {
  id: number;
  doctor_id: number;
  practice_type: string;
  practice_name: string;
  address_line1: string;
  address_line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_number: string;
  website_link?: string | null;
  contact_email?: string | null;
  is_primary: boolean;
  consultation_fee?: number | null;
  followup_fee?: number | null;
  online_consultation_available?: boolean;
  online_consultation_fee?: number | null;
  timings?: DoctorPracticeTiming[];
  full_address?: string;
  created_at?: string;
  updated_at?: string | null;
}

export interface DoctorService {
  id: number;
  doctor_id: number;
  service_id?: number | null;
  custom_service_name?: string | null;
  service_name?: string | null;
  service_description?: string | null;
  service_fee?: number | null;
  duration?: number | null;
  display_order: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string | null;
  /** Category name when returned by API (e.g. DoctorServiceWithDetails) */
  service_category_name?: string | null;
}

export interface DoctorBankDetails {
  id: number;
  doctor_id: number;
  account_holder_name: string;
  masked_account_number?: string;
  ifsc_code: string;
  bank_name?: string | null;
  branch_name?: string | null;
  account_type: string;
  upi_id?: string | null;
  pan_number?: string | null;
  gst_number?: string | null;
  is_verified?: boolean;
  created_at?: string;
  updated_at?: string | null;
}

export type DoctorBankDetailsInput = Omit<
  DoctorBankDetails,
  'id' | 'created_at' | 'updated_at' | 'masked_account_number' | 'is_verified'
> & {
  account_number: string;
};

export type OnboardingStep =
  | 'personalInfo'
  | 'professionalInfo'
  | 'education'
  | 'certifications'
  | 'documents'
  | 'practices'
  | 'services'
  | 'bankDetails'
  | 'bio'
  | 'review';

export const ONBOARDING_STEPS: OnboardingStep[] = [
  'personalInfo',
  'professionalInfo',
  'education',
  'certifications',
  'documents',
  'practices',
  'services',
  'bankDetails',
  'bio',
  'review',
];

export const ONBOARDING_STEP_LABELS: Record<OnboardingStep, string> = {
  personalInfo: 'Personal Information',
  professionalInfo: 'Professional Credentials',
  education: 'Education',
  certifications: 'Certifications',
  documents: 'Documents Upload',
  practices: 'Practice Information',
  services: 'Services Offered',
  bankDetails: 'Bank Details',
  bio: 'Bio & Profile',
  review: 'Review & Submit',
};
